import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { ActiveUserStore } from '../stores/user/active-user.state';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export abstract class CrudService<T> {
  protected abstract basePath: string; // To be defined by extending services
  abstract instanceType: string; // Useful for logging or additional logic
  readonly userStore = inject(ActiveUserStore);
  constructor(protected http: HttpClient) {}

  /**
   * Retrieves the base URL for the API endpoint based on the environment and instance type.
   *
   * @return {string} The constructed base URL string.
   */
  protected getBaseUrl(): string {
    return `${environment.apiUrl}/api/${this.instanceType}`;
  }

  /**
   * Retrieves an item from the server based on the provided identifier.
   *
   * @param {string} id - The unique identifier of the item to retrieve.
   * @return {Promise<T>} A promise that resolves to the retrieved item of type T.
   */
  getItem(id: string) {
    return lastValueFrom(this.http.get<T>(`${this.getBaseUrl()}/${id}`));
  }

  /**
   * Adds an item to the server by making a POST request.
   *
   * @param {T} item - The item to be added.
   * @return {Observable<T>} An observable containing the added item as returned by the server.
   */
  addItem(item: T): Observable<T> {
    return this.http.post<T>(`${this.getBaseUrl()}/insert`, item);
  }

  /**
   * Updates an existing item on the server based on its unique identifier.
   *
   * @param {T & { _id?: string }} item The item to be updated with an optional unique identifier (_id).
   * @return {Observable<T>} An observable representing the updated item from the server.
   */
  updateItem(item: T & { _id?: string }): Observable<T> {
    return this.http.put<T>(`${this.getBaseUrl()}/update/${item._id}`, item);
  }

  /**
   * Updates multiple items by sending a PUT request to the designated endpoint.
   *
   * @param {T[]} items - The array of items to be updated.
   * @return {Observable<T>} An observable that resolves to the updated items.
   */
  updateMultiple(items: T[]): Observable<T> {
    console.log('=========>', items);
    return this.http.put<T>(`${this.getBaseUrl()}/updateMany`, items);
  }

  /**
   * Inserts multiple items into the data source.
   *
   * @param {T[]} items - An array of items to be inserted.
   * @return {Observable<T>} An observable that emits the response of the insert operation.
   */
  insertMultiple(items: T[]): Observable<T> {
    return this.http.post<T>(`${this.getBaseUrl()}/insertMany`, items);
  }

  /**
   * Removes an item using its unique identifier and updates the backend.
   *
   * @param {T & { _id?: string }} item The item to be removed. Must contain an optional `_id` property representing its unique identifier.
   * @return {Observable<void>} An observable that completes when the item is successfully removed.
   */
  removeItem(item: T & { _id?: string }): Observable<void> {
    return this.http.put<void>(`${this.getBaseUrl()}/remove/${item._id}`, item);
  }

  /**
   * Removes multiple items by their IDs.
   *
   * @param {Array} items - An array of items to be removed. Each item must have an `_id` property of type string.
   * @return {Observable<string[] | void>} An observable that emits an array of removed item IDs or void if no response data is present.
   */
  removeMultiple(items: T & { _id: string }[]): Observable<string[] | void> {
    return this.http.put<string[] | void>(
      `${this.getBaseUrl()}/removeMany`,
      items.map(item => item._id)
    );
  }

  /**
   * Fetches items as a promise from a specified endpoint based on the current user's commune.
   *
   * @return {Promise<{ [key: string]: T }>} A promise resolving to an object where keys are strings and values are of type T.
   */
  getItemsAsPromise(): Promise<{ [key: string]: T }> {
    return lastValueFrom(
      this.http.get<{ [key: string]: T }>(
        `${this.getBaseUrl()}/commune/${this.userStore.commune()}`
      )
    );
  }
  /**
   * Fetches items as a promise from a specified endpoint based on the current user's commune.
   *
   * @return {Promise<{ [key: string]: T }>} A promise resolving to an object where keys are strings and values are of type T.
   */
  getItemsByIds(ids: string[]): Promise<{ [key: string]: T }> {
    const headers = new HttpHeaders({
      'X-Ids': JSON.stringify(ids),
    });
    return lastValueFrom(
      this.http.get<{ [key: string]: T }>(`${this.getBaseUrl()}/findByIds`, {
        headers,
      })
    );
  }
}
